import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

/**
 * PageShell — the container and the title block for a top-level app page.
 *
 * ## Why this exists, measured 06.08.2026
 *
 * `ListShell` already owned this for the five list routes, and it owned it
 * well: one width, one rhythm, one h1, heading order by construction. Every
 * OTHER page hand-rolled the same thing, and hand-rolled it differently:
 *
 *   /offers          max-w-[--content-width] · space-y-6 · h1 text-h1   (ListShell)
 *   /calendar        max-w-[--content-width-wide] · space-y-6 · h1 text-h1
 *   /kommunikasjon   max-w-6xl · space-y-4 · h1 text-h2
 *
 * Three routes, three widths (960 · 1200 · 1152), two rhythms and two heading
 * sizes. The 1152 is `max-w-6xl`, a raw Tailwind value matching no token at
 * all. A container width is not decoration: it decides how many cards fit on a
 * row and where every title starts, so three widths read as three different
 * products.
 *
 * Five pages had additionally copied ListShell's header markup verbatim, down
 * to `flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between` and
 * the `mt-1` under the h1 — DashboardPage, CalendarPage, CreateOfferPage,
 * CreateEventPage and PresentationBuilderPage. Five copies of one block is
 * five places to fix the next time it changes, which is husregel 14's own
 * litmus test.
 *
 * ## The rule this makes true by construction
 *
 * A page does not choose a width in a class; it names one from the vocabulary
 * the tokens already document, and it renders no `<h1>` of its own.
 * `check-page-shell.mjs` is the gate: a page-level component that sets its own
 * `max-w-[var(--content-width…)]` container or its own page `<h1>` fails the
 * build. That gate is the point. `KommunikasjonPage` was written AFTER task 18
 * closed «`<h1>` wearing `text-h2`» at zero occurrences, and put the count
 * straight back to one, because task 18 was a grep run once and not a gate.
 *
 * ## What it deliberately does NOT own
 *
 * Page CONTENT. Grids, cards, tables and their gaps stay with the page — this
 * owns the frame around them. And `/messages`, which is full-bleed by design
 * (`AppLayout` gives it a different outlet class), does not use it at all;
 * a shell that had to special-case its own opposite would not be a shell.
 */

/**
 * The four widths, and they are the token file's own vocabulary rather than a
 * new one (`index.css`, `--content-width*`). Naming the intent is the whole
 * mechanism: `width="wide"` is reviewable in a diff, `max-w-6xl` is not.
 */
export type PageWidth = 'default' | 'narrow' | 'wide' | 'data'

const WIDTH_CLASS: Record<PageWidth, string> = {
  /** 960px. Standard app pages. */
  default: 'max-w-[var(--content-width)]',
  /** 720px. Focused views: forms, profile, a single confirmation. */
  narrow: 'max-w-[var(--content-width-narrow)]',
  /** 1200px. Expanded views. The month grid is the reason this exists: seven
   *  columns inside 960px give 137px cells, and a day cell is a layout, not a
   *  label. */
  wide: 'max-w-[var(--content-width-wide)]',
  /** 1280px. Dense admin tables when the rail is collapsed. */
  data: 'max-w-[var(--content-width-data)]',
}

interface PageShellProps {
  /** A standalone page outside the app layout, which supplies no gutter of its own. */
  padded?: boolean
  /**
   * The page's one and only `h1`. Text, not markup: the shell owns the size.
   *
   * Optional, because a few pages ARE their content and name themselves inside
   * it — a deck builder whose title is an editable field, a receipt whose
   * heading is the outcome card. Those still want the container and the
   * rhythm, and omitting the header is the honest way to say «my h1 is
   * elsewhere» rather than rendering a second one.
   */
  title?: ReactNode
  /** One line under the title. */
  summary?: ReactNode
  /** Top-right actions. Wraps under the title below `sm`. */
  actions?: ReactNode
  /**
   * A back control rendered to the LEFT of the title, for pages reached from
   * somewhere specific (`/create-event`, `/offers/create`). It sits inside the
   * header row rather than above it so the title does not shift down by a
   * button's height on exactly those pages.
   */
  back?: ReactNode
  /** Custom content such as an event hero can own its existing vertical rhythm. */
  spacing?: 'default' | 'custom'
  width?: PageWidth
  /** Extra classes on the title block, e.g. `max-lg:sr-only` where a nested panel already names the
   *  page on a phone (settings: «Innstillinger» twice above the panel's own heading). */
  headerClassName?: string
  /** Extra classes on the container. Not a place to put another `max-w`. */
  className?: string
  children: ReactNode
}

/**
 * The page's `h1`, and the ONLY place one is written.
 *
 * `hidden` renders it screen-reader-only. Two app surfaces need that and both
 * are honest cases rather than loopholes: `/messages` is a full-bleed chat with
 * no room for a title bar, and the forced-password-change gate is a single card
 * that IS the page. axe's `page-has-heading-one` still wants an h1 on both, and
 * an h1 that exists only in the accessibility tree is the correct answer to
 * «this page has no visible title», not a reason to hand-roll one.
 */
export function PageTitle({
  children,
  hidden = false,
}: {
  children: ReactNode
  hidden?: boolean
}) {
  return (
    <h1 className={hidden ? 'sr-only' : 'text-h1 text-[var(--text-primary)]'}>{children}</h1>
  )
}

export function PageShell({
  title,
  summary,
  actions,
  back,
  width = 'default',
  spacing = 'default',
  className,
  headerClassName,
  padded = false,
  children,
}: PageShellProps) {
  return (
    <div className={cn('mx-auto', spacing === 'default' && 'space-y-6', WIDTH_CLASS[width], padded && 'p-4 sm:p-6', placed(className, 'PageShell'))}>
      {title != null && (
        // Stacks below sm: at 340px an h1 and a labelled CTA cannot share a
        // row, and side by side the title breaks mid-word instead. Lifted
        // verbatim from ListShell, which is where it was proven.
        <div className={cn('flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between', placed(headerClassName, 'PageShell'))}>
          <div className="flex min-w-0 items-start gap-3 sm:flex-1 sm:basis-48">
            {back}
            <div className="min-w-0">
              <PageTitle>{title}</PageTitle>
              {summary && (
                <p className="text-body text-[var(--text-secondary)] mt-1">{summary}</p>
              )}
            </div>
          </div>
          {actions && (
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
